import React from 'react';
import TextInput from '../Form/TextInput.tsx';
import Select from '../Form/Select.tsx';
import Button from '../Form/Button.tsx';
import { IClientAccount } from '../../interface/IClientAccount.ts';
import { IOption } from '../../interface/IOption.ts';
import { iso3166 } from '../../static/iso3166.js';

interface InlineClientAccountFormProps {
    value:IClientAccount,
    onChange:(next:IClientAccount) => void,
    privateType:IOption,
    companyType:IOption,
    errors?:{ [key:string]:string },
}

/*
 * Slimmed-down, controlled version of ClientAccountForm for entering a new
 * billing account inline in the verification form. Only transfer payment is
 * allowed, so all direct-debit/SEPA/IBAN/certificate fields are dropped. The
 * component does not talk to the API — the parent collects the data and creates
 * the account on submit.
 */
const InlineClientAccountForm = ({
    value,
    onChange,
    privateType,
    companyType,
    errors = {},
}:InlineClientAccountFormProps) => {
    const isCompany = value.client_account_type === companyType.id;

    const handleField = (name:string, fieldValue:any) => {
        onChange({ ...value, [name]: fieldValue });
    };

    return (
        <div className="client-account-form single-account-wrapper inline-client-account">
            <div className="button-pills">
                <h6>Art des Rechnungskontos</h6>
                <div className="button-wrapper">
                    <Button
                        buttonType="primary"
                        type="button"
                        className={value.client_account_type === privateType.id ? '' : 'grey'}
                        onClick={(e:any) => { e.preventDefault(); handleField('client_account_type', privateType.id); }}
                    >{ privateType.label }</Button>
                    <Button
                        buttonType="primary"
                        type="button"
                        className={value.client_account_type === companyType.id ? '' : 'grey'}
                        onClick={(e:any) => { e.preventDefault(); handleField('client_account_type', companyType.id); }}
                    >{ companyType.label }</Button>
                </div>
                { errors.client_account_type && <span className="form-error" role="alert">{ errors.client_account_type }<br /></span> }
            </div>

            <div className={`form-content${!value.client_account_type ? ' disabled' : ''}`}>
                <h6 className='mt-3 mb-2'>Rechnungsadresse</h6>
                <div className="mb-3 d-flex client-account-field-list">
                    <div className="col-6 list">
                        <TextInput
                            wrapperClassName="mb-0-i"
                            onChange={(e:any) => handleField('client_account_billing_name', e.target.value)}
                            value={value.client_account_billing_name}
                            label={`Rechnungsempfänger${isCompany ? ' (Unternehmen)' : ''}`}
                            name="client_account_billing_name"
                            error={errors.client_account_billing_name}
                            required
                        />
                        { isCompany && (
                            <>
                                <TextInput
                                    wrapperClassName="mb-0-i"
                                    onChange={(e:any) => handleField('client_account_department', e.target.value)}
                                    value={value.client_account_department}
                                    label="Rechnungsempfänger (Abteilung)"
                                    name="client_account_department"
                                    error={errors.client_account_department}
                                />
                            </>
                        ) }
                        <TextInput
                            wrapperClassName="mb-0-i"
                            onChange={(e:any) => handleField('client_account_address', e.target.value)}
                            value={value.client_account_address}
                            label="Straße, Hausnummer"
                            name="client_account_address"
                            error={errors.client_account_address}
                            required
                        />
                        <TextInput
                            wrapperClassName="mb-0-i"
                            onChange={(e:any) => handleField('client_account_zip', e.target.value)}
                            value={value.client_account_zip}
                            label="PLZ"
                            name="client_account_zip"
                            error={errors.client_account_zip}
                            required
                        />
                        <TextInput
                            wrapperClassName="mb-0-i"
                            onChange={(e:any) => handleField('client_account_city', e.target.value)}
                            value={value.client_account_city}
                            label="Ort"
                            name="client_account_city"
                            error={errors.client_account_city}
                            required
                        />
                        <Select
                            wrapperClassName="mb-0-i"
                            isSearchable
                            isClearable
                            label="Land"
                            placeholder="Land auswählen"
                            name="client_account_country"
                            value={value.client_account_country}
                            options={iso3166}
                            onChange={(option:any, action:any) => {
                                if (option && option.value) {
                                    handleField('client_account_country', option);
                                } else if (!option && action.action === 'clear') {
                                    handleField('client_account_country', null);
                                }
                            }}
                            error={errors.client_account_country}
                            isRequired
                        />
                        <TextInput
                            wrapperClassName="mb-0-i"
                            onChange={(e:any) => handleField('client_account_email', e.target.value)}
                            value={value.client_account_email}
                            label="E-Mail"
                            name="client_account_email"
                            error={errors.client_account_email}
                            required
                        />
                    </div>
                    <div className="col-6 list">
                        { isCompany && (
                            <>
                                <TextInput
                                    wrapperClassName="mb-0-i"
                                    onChange={(e:any) => handleField('client_account_ust_id', e.target.value)}
                                    value={value.client_account_ust_id}
                                    label="UST-ID / VAT"
                                    name="client_account_ust_id"
                                    error={errors.client_account_ust_id}
                                />
                                <TextInput
                                    wrapperClassName="mb-0-i"
                                    onChange={(e:any) => handleField('client_account_contact_person', e.target.value)}
                                    value={value.client_account_contact_person}
                                    label="Rechnungsempfänger (Ansprechpartner)"
                                    name="client_account_contact_person"
                                    error={errors.client_account_contact_person}
                                />
                                <TextInput
                                    wrapperClassName="mb-0-i"
                                    onChange={(e:any) => handleField('client_account_cost_position', e.target.value)}
                                    value={value.client_account_cost_position}
                                    label="Kostenstelle"
                                    name="client_account_cost_position"
                                    error={errors.client_account_cost_position}
                                />
                                <TextInput
                                    wrapperClassName="mb-0-i"
                                    onChange={(e:any) => handleField('client_account_project_number', e.target.value)}
                                    value={value.client_account_project_number}
                                    label="Projektnummer"
                                    name="client_account_project_number"
                                    error={errors.client_account_project_number}
                                />
                                <TextInput
                                    wrapperClassName="mb-0-i"
                                    onChange={(e:any) => handleField('client_account_budget_number', e.target.value)}
                                    value={value.client_account_budget_number}
                                    label="Budget-Nr."
                                    name="client_account_budget_number"
                                    error={errors.client_account_budget_number}
                                />
                                <TextInput
                                    wrapperClassName="mb-0-i"
                                    onChange={(e:any) => handleField('client_account_address_extra', e.target.value)}
                                    value={value.client_account_address_extra}
                                    label="Rechnungsempfänger (Adresszusatz)"
                                    name="client_account_address_extra"
                                    error={errors.client_account_address_extra}
                                />
                            </>
                        ) }
                    </div>
                    
                </div>
            </div>
        </div>
    );
};

export default InlineClientAccountForm;
